import { TREE_LOG_ITEM_IDS, TREE_PLANK_ITEM_IDS } from '../game-data.js';

const TREE_LOG_ITEM_ID_SET = new Set(TREE_LOG_ITEM_IDS);
const TREE_PLANK_ITEM_ID_SET = new Set(TREE_PLANK_ITEM_IDS);

function isExpectedItemMatch(expected, actual) {
  if (expected === null) return actual === null;
  if (actual === null) return false;

  if (Array.isArray(expected)) {
    return expected.includes(actual);
  }

  if (typeof expected === 'object' && expected !== null) {
    if (Array.isArray(expected.anyOf)) {
      return expected.anyOf.includes(actual);
    }
    if (expected.group === 'tree_logs') {
      return TREE_LOG_ITEM_ID_SET.has(actual);
    }
    if (expected.group === 'tree_planks') {
      return TREE_PLANK_ITEM_ID_SET.has(actual);
    }
  }

  if (expected === '3') return TREE_LOG_ITEM_ID_SET.has(actual);
  if (expected === '6') return TREE_PLANK_ITEM_ID_SET.has(actual);
  return actual === expected;
}

function normalizePattern(patternRows) {
  const rows = patternRows.map((row) => Array.from(row));
  const width = rows.reduce((maxWidth, row) => Math.max(maxWidth, row.length), 0);
  const padded = rows.map((row) => {
    const out = row.slice();
    while (out.length < width) out.push(' ');
    return out;
  });

  let minX = width;
  let minY = padded.length;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < padded.length; y++) {
    for (let x = 0; x < width; x++) {
      if (padded[y][x] === ' ') continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  if (maxX < minX || maxY < minY) {
    return { width: 0, height: 0, cells: [] };
  }

  const cells = [];
  for (let y = minY; y <= maxY; y++) {
    const row = [];
    for (let x = minX; x <= maxX; x++) row.push(padded[y][x]);
    cells.push(row);
  }

  return {
    width: maxX - minX + 1,
    height: maxY - minY + 1,
    cells
  };
}

function mirrorCells(cells) {
  return cells.map((row) => row.slice().reverse());
}

function buildCraftIdGrid(craftMode, craftSlots, isEmptyStack) {
  const size = craftMode === 'table' ? 3 : 2;
  const grid = craftSlots[craftMode];
  const ids = [];

  for (let y = 0; y < size; y++) {
    const row = [];
    for (let x = 0; x < size; x++) {
      const idx = y * size + x;
      row.push(isEmptyStack(grid[idx]) ? null : grid[idx].id);
    }
    ids.push(row);
  }

  return { size, ids };
}

function buildShapelessIngredients(recipe) {
  if (Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0) {
    return recipe.ingredients.slice();
  }

  const normalized = normalizePattern(recipe.pattern || []);
  if (normalized.width === 0 || normalized.height === 0) return [];

  const ingredients = [];
  for (const row of normalized.cells) {
    for (const ch of row) {
      if (ch === ' ') continue;
      const expected = recipe.key?.[ch] || null;
      if (expected !== null) ingredients.push(expected);
    }
  }
  return ingredients;
}

function matchShapelessRecipe(recipe, size, ids) {
  const ingredients = buildShapelessIngredients(recipe);
  if (ingredients.length === 0 || ingredients.length > size * size) return null;

  const actualEntries = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const actual = ids[y][x];
      if (actual === null) continue;
      actualEntries.push({ slot: y * size + x, id: actual });
    }
  }

  if (actualEntries.length !== ingredients.length) return null;

  const used = new Array(actualEntries.length).fill(false);
  const consume = [];
  const search = (depth = 0) => {
    if (depth >= ingredients.length) return true;
    const expected = ingredients[depth];
    for (let i = 0; i < actualEntries.length; i++) {
      if (used[i]) continue;
      if (!isExpectedItemMatch(expected, actualEntries[i].id)) continue;
      used[i] = true;
      consume.push(actualEntries[i].slot);
      if (search(depth + 1)) return true;
      consume.pop();
      used[i] = false;
    }
    return false;
  };

  if (!search(0)) return null;
  return {
    recipe,
    consume
  };
}

function matchRecipe(recipe, size, ids) {
  if (recipe.station === 'table' && size !== 3) return null;
  if (recipe.station !== 'table' && !['hand', 'any'].includes(recipe.station || 'hand')) return null;

  if (recipe.shapeless) {
    return matchShapelessRecipe(recipe, size, ids);
  }

  const normalized = normalizePattern(recipe.pattern || []);
  if (normalized.width === 0 || normalized.height === 0) return null;
  if (normalized.width > size || normalized.height > size) return null;

  const variants = [{ cells: normalized.cells, mirrored: false }];
  if (recipe.mirror !== false) {
    variants.push({ cells: mirrorCells(normalized.cells), mirrored: true });
  }

  for (const variant of variants) {
    for (let offY = 0; offY <= size - normalized.height; offY++) {
      for (let offX = 0; offX <= size - normalized.width; offX++) {
        let ok = true;
        const consume = [];

        for (let y = 0; y < size && ok; y++) {
          for (let x = 0; x < size; x++) {
            const inside = x >= offX && x < offX + normalized.width && y >= offY && y < offY + normalized.height;
            let expected = null;

            if (inside) {
              const ch = variant.cells[y - offY][x - offX];
              expected = ch === ' ' ? null : recipe.key?.[ch] || null;
            }

            const actual = ids[y][x];
            if (!isExpectedItemMatch(expected, actual)) {
              ok = false;
              break;
            }
            if (expected !== null) {
              consume.push(y * size + x);
            }
          }
        }

        if (ok) {
          return {
            recipe,
            consume
          };
        }
      }
    }
  }

  return null;
}

export function findCraftResult({ recipes, craftMode, craftSlots, isEmptyStack }) {
  const { size, ids } = buildCraftIdGrid(craftMode, craftSlots, isEmptyStack);

  for (const recipe of recipes) {
    const hit = matchRecipe(recipe, size, ids);
    if (!hit) continue;

    const outEntries = Object.entries(recipe.output || {});
    if (outEntries.length === 0) continue;

    const [itemId, count] = outEntries[0];
    return {
      itemId,
      count,
      consume: hit.consume,
      station: recipe.station,
      recipeId: recipe.id
    };
  }

  return null;
}
